import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Card, Col, Input, InputNumber, Row, Tooltip } from 'antd';
import { useTranslation, withTranslation } from 'react-i18next';
import styled from 'styled-components';
import { debounce } from 'underscore';
import EditorElement from '../../../../components/EditorElement';
import { PAGE_NUMBER } from '../../../../utils/complexDocumentConstants';
import { getPageNoAdditionalContent, pageNumberList } from './pageNumberHelper';
import {
  COMPLEX_DOCUMENT_CONTEXT_KEYS,
  useDocumentContext,
} from '../../../../context/complexDocumentContext';
import { PageLayoutInputLabel } from '../../styles';
import {
  rgb2Hex,
  hexToRgbArr,
  measurementConversion,
  deepCopy,
} from '../../../../utils/javascript';
import ColorPicker from '../../../../artboard/ColorPicker';
import { PAGE_NUMBER_TRANS } from '../../translationValues';

const NAMESPACES = ['PAGE_NUMBER_TRANS'];
const DEBOUNCE_TIMEOUT = 300;

const CardContainer = styled.div`
  .ant-card {
    background: black;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    font-size: 14px;
    font-family: system-ui;
    border-radius: 12px;
    padding: 0px;
    .ant-card-body {
      padding: 0px;
      flex-grow: 1;
      text-align: center;
      > div {
        padding: 20px 0px;
        :hover {
          background: hsla(0, 0%, 100%, 0);
        }
      }
    }
  }
`;

const PgNoColorPickerContainer = styled.span`
  height: 16px;
  width: 16px;
  position: relative;
  cursor: pointer;
  margin-top: 8px;
  > div {
    position: absolute;
    z-index: 11;
    margin-top: 20px;
    margin-left: ${({pageNumberType}) => pageNumberType === 1 ? '-22px' : '-148px'};
  }
`;

const PgNoColorPicker = ({ fontColor, onColorChange, pageNumberType }) => {
  const [showPicker, setShowPicker] = useState(false);

  return (
    <PgNoColorPickerContainer
      style={{
        backgroundColor: rgb2Hex(fontColor),
      }}
      pageNumberType={pageNumberType}
      onClick={() => setShowPicker(true)}
    >
      {showPicker && (
        <div>
          <ColorPicker
            onChange={color => onColorChange(color, fontColor)}
            onBlur={() => setShowPicker(false)}
            color={rgb2Hex(fontColor)}
            qlEditor={true}
          />
        </div>
      )}
    </PgNoColorPickerContainer>
  );
};

const PageNumberSettings = ({ flowUUID, sizeIn }) => {
  const { t } = useTranslation(NAMESPACES);
  const flowSelector = useCallback(store => store[flowUUID], [flowUUID]);
  const [flow, methods] = useDocumentContext(flowSelector, `PageNumberSettings`);
  const pageNumberUUID = flow[PAGE_NUMBER];
  const pageNumberSelector = useCallback(store => store[pageNumberUUID], [pageNumberUUID]);
  const [pageNumber] = useDocumentContext(pageNumberSelector, `PageNumberSettings`);
  const docPageNumberSelector = useCallback(
    state => {
      const styles = state[COMPLEX_DOCUMENT_CONTEXT_KEYS.STYLES];
      if (styles) {
        return styles[PAGE_NUMBER];
      }
      return null;
    },
    [pageNumberUUID],
  );
  const [docPageNumber] = useDocumentContext(docPageNumberSelector);
  const hiddenRef = useRef();
  const pageNumberType = docPageNumber?.pageNumberType;
  const ofText = docPageNumber?.ofText;
  const contents = docPageNumber?.contents?.contents;
  const style = pageNumber?.contents?.style;
  const isMM = sizeIn === 'mm';

  useEffect(() => {
    setText(contents?.[0]?.content);
    setLeft(style?.x);
    setTop(style?.y);
  }, [contents, style]);

  const [text, setText] = useState(contents?.[0]?.content);
  const [left, setLeft] = useState(style?.x);
  const [top, setTop] = useState(style?.y);

  const debouncedUpdatePageNumber = useCallback(
    debounce(methods.updatePageNumber, DEBOUNCE_TIMEOUT),
    [],
  );

  const updatePageNumberWidthNContent = obj => {
    obj.style = { width: hiddenRef.current.scrollWidth };
    methods.updatePageNumber(obj);
  };

  const debouncedUpdatePgNoWidthNContent = useCallback(
    debounce(updatePageNumberWidthNContent, DEBOUNCE_TIMEOUT),
    [],
  );

  const elements = pageNumberList(t);

  const handleCardClick = ({ pageNumberType: type, contents: defaultContents }) => {
    // return if same page number format is clicked again.
    if (type === pageNumberType) {
      methods.deletePageNumber({ uuid: flowUUID });
      return;
    }
    const pageNumberObj = { pageNumberType: type };
    // if clicked or current page number format is type 1, then copy contents to the store page number
    if (type === 1 || pageNumberType === 1) {
      defaultContents.contents[0].style = contents?.[0].style;
      pageNumberObj.contents = defaultContents.contents;
    }
    methods.updatePageNumber({ uuid: flowUUID, ...pageNumberObj, historyChange: {} });
  };

  const updateLeft = value => {
    const convertedValue = isMM ? measurementConversion(value, 'px') : value;
    setLeft(convertedValue);
    debouncedUpdatePageNumber({ uuid: flowUUID, style: { x: convertedValue }, historyChange: {} });
  };

  const updatetop = value => {
    const convertedValue = isMM ? measurementConversion(value, 'px') : value;
    setTop(convertedValue);
    debouncedUpdatePageNumber({ uuid: flowUUID, style: { y: convertedValue }, historyChange: {} });
  };

  const updatePageText = event => {
    const text = event.target.value;
    const contentCopy = deepCopy(contents);
    contentCopy[0].content = text;
    setText(text);
    debouncedUpdatePgNoWidthNContent({ uuid: flowUUID, contents: contentCopy, historyChange: {} });
  };

  const updateColor = ({ rgb }) => {
    const contentCopy = deepCopy(contents);
    contentCopy[0].style = { ...contentCopy[0].style, color: rgb2Hex([rgb.r, rgb.g, rgb.b]) };
    debouncedUpdatePageNumber({ uuid: flowUUID, contents: contentCopy, historyChange: {} });
  };

  const additionalContent = getPageNoAdditionalContent(pageNumberType, ofText);

  return (
    <>
      {pageNumber && docPageNumber && (
        <div className="properties-container">
          <div className="grid">
            <div className="input-container">
              <PageLayoutInputLabel>
                {t(PAGE_NUMBER_TRANS.LEFT_LABEL.key, PAGE_NUMBER_TRANS.LEFT_LABEL.value)}
              </PageLayoutInputLabel>
              <InputNumber
                type="number"
                size="large"
                value={isMM ? measurementConversion(left, 'mm') : left}
                min={0}
                max={10000}
                onChange={updateLeft}
              />
            </div>
            <div className="input-container">
              <PageLayoutInputLabel>
                {t(PAGE_NUMBER_TRANS.TOP_LABEL.key, PAGE_NUMBER_TRANS.TOP_LABEL.value)}
              </PageLayoutInputLabel>
              <InputNumber
                type="number"
                size="large"
                min={0}
                max={10000}
                value={isMM ? measurementConversion(top, 'mm') : top}
                onChange={updatetop}
              />
            </div>
          </div>
          <div className="grid">
            {pageNumberType !== 1 && (
              <div className="input-container">
                <PageLayoutInputLabel>
                  {t(PAGE_NUMBER_TRANS.TEXT_LABEL.key, PAGE_NUMBER_TRANS.TEXT_LABEL.value)}
                </PageLayoutInputLabel>
                <Input value={text} onChange={updatePageText} />
              </div>
            )}
            <div className="input-container">
              <PageLayoutInputLabel>
                {t(PAGE_NUMBER_TRANS.COLOR_LABEL.key, PAGE_NUMBER_TRANS.COLOR_LABEL.value)}
              </PageLayoutInputLabel>
              <PgNoColorPicker
                fontColor={hexToRgbArr(contents?.[0]?.style?.color)}
                onColorChange={updateColor}
                pageNumberType={pageNumberType}
              />
            </div>
          </div>
        </div>
      )}
      <div
        style={{
          height: '1px',
          whiteSpace: 'nowrap',
          visibility: 'hidden',
          overflow: 'visible',
          ...contents?.[0]?.style,
        }}
        ref={hiddenRef}
      >
        {text}
        {additionalContent}
      </div>
      <CardContainer>
        <Row gutter={[16, 16]} style={{ margin: '0px' }}>
          {elements?.map(element => {
            const isSelected = element.elementDetails.pageNumberType === pageNumberType;
            return (
              <Col span={12}>
                <Tooltip
                  placement="top"
                  title={t(
                    PAGE_NUMBER_TRANS.DRAG_DROP_INFO.key,
                    PAGE_NUMBER_TRANS.DRAG_DROP_INFO.value,
                  )}
                >
                  <Card
                    bordered={true}
                    style={{
                      border: isSelected ? '1px red solid' : '',
                    }}
                    onClick={() => (!!docPageNumber ? handleCardClick(element.elementDetails) : '')}
                  >
                    <EditorElement
                      element={element}
                      clickElement={() => {}}
                      pageSettings={{}}
                      toggleDragging={() => {}}
                      setIsDragAndDropped={() => {}}
                      draggable={!docPageNumber ? true : false}
                      type={PAGE_NUMBER}
                    />
                  </Card>
                </Tooltip>
              </Col>
            );
          })}
        </Row>
      </CardContainer>
    </>
  );
};
export default withTranslation(NAMESPACES)(PageNumberSettings);
